<script lang="ts">
  import { onMount } from "svelte";
  import { storyblokEditable } from "@storyblok/svelte";
  import { deviceDetails } from "$lib/consumer/stores/deviceDetails";
  import GetDeliveryButton from "./GetDeliveryButton.svelte";
  import gsap from "gsap";
  import { dev } from "$app/environment";

  // Get the block data
  export let blok;

  // Get the device details
  $: ({ isMobile, isTablet, isDesktop } = $deviceDetails);

  // Declare element references
  let droneCanvas: HTMLCanvasElement;
  let gsapContext: gsap.Context;

  // Initialize state
  let currentDeviceType: string;
  let animationReady = false;

  /**
   * Starts the drone animation
   */
  function startAnimation() {
    if (!droneCanvas) return;

    // Set the canvas context
    const context = droneCanvas.getContext("2d");
    if (!context) return;

    // Set the canvas size based on the viewport dimensions
    droneCanvas.width = isMobile ? 400 : 800;
    droneCanvas.height = isMobile ? 400 : 800;

    // Set the animation sequence and frames
    let sequence = { frame: 0 };
    let frames = { start: 0, end: 154, loop: 130 };

    // Derive the image path from the device type
    let imagePath = isTablet || isDesktop ? "desktop" : "mobile";

    // Define variables to handle image preloading
    let loadedImagesCount = 0;
    const totalImages = frames.end + 1; // +1 because we're loading from 0 to frames.end
    const images: HTMLImageElement[] = [];

    // Preload a single image
    const loadImage = (index: number) => {
      const image = new Image();
      image.onload = () => {
        loadedImagesCount++;

        // If this is the first image, display it immediately
        if (index === 0 && context) {
          context.clearRect(0, 0, droneCanvas.width, droneCanvas.height);
          context.drawImage(image, 0, 0);
        }

        // Start the animation only when all images are loaded
        if (loadedImagesCount === totalImages) {
          startAnimation();
        }
      };

      image.src = `/images/technology/drone-sequence/${imagePath}/${(index + 1)
        .toString()
        .padStart(4, "0")}.webp`;

      return image;
    };

    // Preload all images
    for (let i = 0; i <= frames.end; i++) {
      images[i] = loadImage(i);
    }

    // Start the animation
    function startAnimation() {
      animationReady = true;

      // Fade and slide the drone into position
      gsap.fromTo(
        droneCanvas,
        {
          opacity: 0,
        },
        {
          duration: 1,
          opacity: 1,
        },
      );

      gsap.fromTo(
        droneCanvas,
        {
          y: isMobile ? -30 : -50,
        },
        {
          duration: 2,
          ease: "power1.out",
          y: 0,
        },
      );

      // Play the image sequence
      gsap.to(sequence, {
        frame: frames.loop,
        snap: "frame",
        ease: "none",
        duration: 5,
        onUpdate: () => {
          if (droneCanvas && context) {
            context.clearRect(0, 0, droneCanvas.width, droneCanvas.height);
            context.drawImage(images[sequence.frame], 0, 0);
          }
        },
        onComplete: () => {
          sequence.frame = frames.loop;

          // Loop the last few frames for the remainder of the animation
          gsap.to(sequence, {
            frame: frames.end,
            snap: "frame",
            ease: "none",
            duration: 1,
            repeat: -1,
            yoyo: true,
            onUpdate: () => {
              if (droneCanvas && context) {
                context.clearRect(0, 0, droneCanvas.width, droneCanvas.height);
                context.drawImage(images[sequence.frame], 0, 0);
              }
            },
          });
        },
      });
    }
  }

  /**
   * Initialize the animation context and start the animation
   */
  function initAnimation() {
    gsapContext = gsap.context(() => {
      startAnimation();
    });
  }

  /**
   * Cleanup the animation context
   */
  function cleanupAnimation() {
    if (gsapContext) {
      gsapContext.revert();
    }
  }

  // Mount
  onMount(() => {
    initAnimation();

    // Unmount
    return () => {
      cleanupAnimation();
    };
  });
</script>

<section class="tech-hero-section" use:storyblokEditable={blok}>
  <div class="content-wrapper section-header-width mx-auto">
    <div class="text-wrapper">
      <h1 class="main-heading">{blok.heading}</h1>
      <h1 class="main-subheading">{blok.subheading}</h1>
      <GetDeliveryButton {blok} />
    </div>
    <div class="animation-wrapper" class:visible={animationReady}>
      <canvas id="drone-canvas" bind:this={droneCanvas} />
    </div>
  </div>
  <div class="clouds"></div>
</section>

<style lang="postcss">
  .tech-hero-section {
    position: relative;
    margin-top: 40px;
    margin-bottom: 2rem;

    @screen md {
      max-height: 900px;
    }
  }

  .content-wrapper {
    position: relative;
    top: 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
      "animation"
      "text";
    z-index: 10;

    @screen md {
      height: 85vh;
    }

    @screen lg {
      height: 90vh;
      grid-template-columns: 40% 60%;
      grid-template-areas: "text animation";
    }
  }

  .text-wrapper {
    grid-area: text;

    @screen md {
      transform: translateY(-9rem);
    }

    @screen lg {
      transform: none;
      margin-top: 150px;
    }
  }

  .animation-wrapper {
    visibility: hidden;
    height: 350px;
    margin-left: auto;
    margin-right: auto;
    transform: translateY(2rem);
    grid-area: animation;

    @media (min-width: 385px) {
      height: 365px;
    }

    @media (min-width: 410px) {
      height: 410px;
    }

    @screen md {
      height: auto;
      transform: scale(0.9) translateY(3rem);
    }

    &.visible {
      visibility: visible;
    }
  }

  .main-heading {
    font-size: 64px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1.1;
    color: #fff;

    @screen md {
      font-size: 76px;
      line-height: 1;
    }

    @screen lg {
      font-size: 88px;
    }
  }

  .main-subheading {
    margin-top: 2.5rem;
    margin-bottom: 2rem;
    font-size: 18px;
    line-height: 160%;
    font-weight: 300;
    color: #fff;
    white-space: pre-wrap;

    @screen md {
      font-size: 20px;
    }
  }

  /* Override get delivery button styles */
  .tech-hero-section :global(.get-delivery-button-wrapper),
  .tech-hero-section :global(.get-delivery-button) {
    margin-left: 0;
    margin-right: 0;
  }

  .clouds {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    background-image: url("/images/technology/hero-clouds-mobile.webp");
    background-repeat: no-repeat;
    background-size: 150vw;
    background-position: center top;
    opacity: 0.4;
    z-index: 5;
    animation: cloud-scale-mobile 2s forwards;

    @screen md {
      max-height: 900px;
      background-size: 100vw;
    }

    @screen lg {
      background-image: url("/images/technology/hero-clouds-desktop.webp");
      background-size: contain;
      background-position: 40vw top;
      animation: cloud-scale-desktop 2.5s ease forwards;
    }
  }

  @keyframes cloud-scale-mobile {
    0% {
      opacity: 0.4;
      transform: translateY(20px);
    }
    100% {
      opacity: 0.9;
      transform: translateY(0);
    }
  }

  @keyframes cloud-scale-desktop {
    0% {
      opacity: 0.4;
      transform: translateY(40px);
    }
    100% {
      opacity: 0.9;
      transform: translateY(0);
    }
  }
</style>
